import React, { useState, useEffect, useRef } from "react";
import { Link } from "../components/L";
import axios from "axios";
import useSEO from "../hooks/useSEO";
import { resolveAssetUrl } from "../utils/assets";
import { useLanguage } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const useScrollReveal = (threshold = 0.1) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.unobserve(el); } }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
};

const TrainerCard = ({ trainer, index }) => {
  const { tx } = useLanguage();
  const [ref, visible] = useScrollReveal();
  const imgSrc = resolveAssetUrl(trainer.image_url, "https://images.pexels.com/photos/23625642/pexels-photo-23625642.jpeg");

  return (
    <Link
      ref={ref}
      to={`/trajnere/${trainer.slug || trainer.id}`}
      className="relative bg-white border border-slate-200 rounded-3xl overflow-hidden group text-center block trainer-card-fx"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(40px)',
        transition: `opacity 0.7s ease ${index * 0.12}s, transform 0.7s ease ${index * 0.12}s`,
      }}
      data-testid={`trainer-card-${index}`}
    >
      <style>{`
        @keyframes trainerHaloSpin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes trainerGlowPulse {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }
        .trainer-card-fx {
          transition: transform 0.55s cubic-bezier(0.22, 1, 0.36, 1),
                      box-shadow 0.55s cubic-bezier(0.22, 1, 0.36, 1),
                      border-color 0.4s ease;
          will-change: transform;
        }
        .trainer-card-fx:hover {
          transform: translateY(-12px) scale(1.015);
          box-shadow: 0 28px 60px -20px rgba(8, 112, 122, 0.35),
                      0 12px 30px -12px rgba(0, 63, 114, 0.18);
          border-color: rgba(28, 163, 176, 0.35);
        }
        .trainer-card-fx::before {
          content: '';
          position: absolute;
          inset: 0;
          background: radial-gradient(circle at var(--mx, 50%) var(--my, 50%),
                      rgba(60, 212, 225, 0.10), transparent 55%);
          opacity: 0;
          transition: opacity 0.4s ease;
          pointer-events: none;
        }
        .trainer-card-fx:hover::before { opacity: 1; }
        .trainer-card-fx .halo {
          position: absolute;
          inset: -3px;
          border-radius: 9999px;
          background: conic-gradient(from 0deg, #3CD4E1, #1CA3B0, #003F72, #1CA3B0, #3CD4E1);
          opacity: 0;
          transition: opacity 0.5s ease;
          filter: blur(2px);
        }
        .trainer-card-fx:hover .halo {
          opacity: 1;
          animation: trainerHaloSpin 6s linear infinite, trainerGlowPulse 2.4s ease-in-out infinite;
        }
        .trainer-card-fx .photo-wrap {
          transition: transform 0.6s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .trainer-card-fx:hover .photo-wrap { transform: scale(1.06); }
        .trainer-card-fx .photo {
          transition: transform 0.9s cubic-bezier(0.22, 1, 0.36, 1), filter 0.5s ease;
        }
        .trainer-card-fx:hover .photo {
          transform: scale(1.15);
          filter: saturate(1.1) contrast(1.03);
        }
        .trainer-card-fx .name-anim {
          background-image: linear-gradient(90deg, #1CA3B0, #1CA3B0);
          background-size: 0% 2px;
          background-repeat: no-repeat;
          background-position: 50% 100%;
          transition: background-size 0.45s ease;
        }
        .trainer-card-fx:hover .name-anim { background-size: 70% 2px; }
        .trainer-card-fx .cta-arrow {
          transition: transform 0.35s ease, letter-spacing 0.35s ease;
        }
        .trainer-card-fx:hover .cta-arrow {
          transform: translateX(6px);
          letter-spacing: 0.02em;
        }
        .trainer-card-fx .specialty-chip {
          transition: background 0.35s ease, color 0.35s ease;
        }
        .trainer-card-fx:hover .specialty-chip {
          background: linear-gradient(130deg, rgba(60,212,225,0.18), rgba(0,63,114,0.18));
          color: #003F72;
        }
      `}</style>

      <div
        onMouseMove={e => {
          const rect = e.currentTarget.getBoundingClientRect();
          e.currentTarget.parentElement.style.setProperty('--mx', `${((e.clientX - rect.left) / rect.width) * 100}%`);
          e.currentTarget.parentElement.style.setProperty('--my', `${((e.clientY - rect.top) / rect.height) * 100}%`);
        }}
        className="pt-12 pb-7 relative"
      >
        <div className="relative w-52 h-52 mx-auto photo-wrap">
          <div className="halo" />
          <div className="relative w-full h-full rounded-full overflow-hidden border-[5px] border-white shadow-[0_8px_30px_rgba(8,112,122,0.18)]">
            <img src={imgSrc} alt={trainer.name} className="w-full h-full object-cover photo" />
          </div>
        </div>
      </div>
      <div className="px-7 pb-9 relative">
        <h3 className="text-2xl font-bold tracking-tight mb-2 inline-block name-anim pb-0.5" style={{ color: 'rgb(28,163,176)', fontFamily: "'Noto Sans', sans-serif" }}>
          {trainer.name}
        </h3>
        <p className="specialty-chip text-[11px] font-bold uppercase tracking-[0.18em] text-[#08707A] mb-4 inline-block px-3 py-1 rounded-full bg-[#1CA3B0]/8" style={{ background: 'rgba(28,163,176,0.08)' }}>
          {trainer.specialty}
        </p>
        <p className="text-sm text-[#4A5568] leading-relaxed mb-5 min-h-[60px]">
          {trainer.short_bio || trainer.bio}
        </p>
        <span className="text-sm font-semibold inline-flex items-center gap-1.5 cta-arrow" style={{ color: '#1CA3B0' }}>
          {tx("Shiko Profilin")} <i className="fas fa-arrow-right text-xs"></i>
        </span>
      </div>
    </Link>
  );
};

const TrainersPage = () => {
  const { t, tx } = useLanguage();
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [heroVisible, setHeroVisible] = useState(false);

  useSEO({
    title: tx("Trajnere - Eksperte Nderkombetare"),
    description: tx("Njihuni me trajneret nderkombetare te Albanian Aligners Academy - doktore dhe specialiste me pervoje boterore ne implantologji, ortodonci dhe dentistri estetike."),
    keywords: "trajnere dentare, eksperte ortodoncie, lektore implantologji, doktore nderkombetare, Albanian Aligners Academy trajnere",
    canonical: "https://aligners.academy/trajnere",
  });

  useEffect(() => {
    setTimeout(() => setHeroVisible(true), 100);
    axios.get(`${API}/trainers`).then(r => { setTrainers(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  return (
    <div data-testid="trainers-page">
      {/* Hero Banner */}
      <section className="relative py-10 md:py-14 px-6" style={{ background: 'linear-gradient(130deg, #3CD4E1 0%, #003F72 100%)' }} data-testid="trainers-hero">
        <div className="max-w-[1350px] mx-auto">
          <div className="flex items-center gap-2 text-white/70 text-sm mb-4" style={{ opacity: heroVisible ? 1 : 0, transform: heroVisible ? 'translateY(0)' : 'translateY(15px)', transition: 'all 0.6s ease' }}>
            <Link to="/" className="hover:text-white text-white/70">{t('breadcrumb_home')}</Link>
            <span className="text-white/70">/</span>
            <span className="text-white">{t('breadcrumb_trainers')}</span>
          </div>
          <h1 className="text-white mb-3" style={{ fontFamily: "'Noto Sans', sans-serif", fontWeight: 700, fontSize: '48px', lineHeight: 1.2, opacity: heroVisible ? 1 : 0, transform: heroVisible ? 'translateY(0)' : 'translateY(20px)', transition: 'all 0.7s ease 0.1s' }} data-testid="trainers-title">
            {t('breadcrumb_trainers')}
          </h1>
          <p className="text-white/80 max-w-2xl" style={{ opacity: heroVisible ? 1 : 0, transform: heroVisible ? 'translateY(0)' : 'translateY(20px)', transition: 'all 0.7s ease 0.2s' }}>
            {t('trainers_hero_desc')}
          </p>
        </div>
      </section>

      {/* Trainers Grid */}
      <section className="py-20 md:py-28 px-6 md:px-12 max-w-[1350px] mx-auto" data-testid="trainers-grid-section">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-2xl border border-slate-200 h-96 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {trainers.map((trainer, i) => (
              <TrainerCard key={trainer.id} trainer={trainer} index={i} />
            ))}
          </div>
        )}
      </section>

      {/* CTA */}
      <section className="relative py-20 overflow-hidden" style={{ background: '#ffffff' }} data-testid="trainers-cta">
        <style>{`
          @keyframes trainersCtaGradient {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
        `}</style>
        <div className="relative z-10 max-w-[1350px] mx-auto px-6 text-center">
          <h2
            className="text-2xl sm:text-3xl font-bold tracking-tight mb-6"
            style={{
              fontFamily: "'Noto Sans', sans-serif",
              background: 'linear-gradient(130deg, #3CD4E1 0%, #1CA3B0 25%, #003F72 50%, #1CA3B0 75%, #3CD4E1 100%)',
              backgroundSize: '300% 300%',
              animation: 'trainersCtaGradient 8s ease infinite',
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              color: 'transparent',
            }}
          >
            {tx("Deshironi te mesoni nga ekspertet me te mire?")}
          </h2>
          <p className="text-[#4A5568] mb-10 max-w-xl mx-auto">
            {tx("Regjistrohuni per eventet tona te ardhshme dhe perfitoni nga eksperienca e tyre nderkombetare.")}
          </p>
          <Link to="/evente" className="rounded-md px-8 py-3.5 font-semibold inline-flex items-center gap-2 text-white transition-transform hover:-translate-y-0.5" style={{ background: 'linear-gradient(130deg, #3CD4E1 0%, #003F72 100%)' }} data-testid="trainers-cta-btn">
            {tx("Shiko Eventet")} <i className="fas fa-arrow-right text-sm"></i>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default TrainersPage;
